/**
 * Mock data for storefront pages — used when the API is unavailable (no backend).
 * All amounts are in FCFA. All text visible to user is in French.
 *
 * Data lives in JSON fixtures under ./fixtures/ and is hydrated here
 * with dynamic dates computed at import time.
 */

import heroSlidesFixture from "./fixtures/mock-hero-slides.json";
import brandShowcasesFixture from "./fixtures/mock-brand-showcases.json";
import deliveryFixture from "./fixtures/mock-delivery.json";
import productsFixture from "./fixtures/mock-products.json";
import cmsFixture from "./fixtures/mock-cms.json";

// ─── Date helpers ───────────────────────────────────────────────────

/** Convert an offset in days (0 = now, -1 = yesterday, etc.) to an ISO string. */
function resolveOffset(days: number): string {
  return new Date(Date.now() + days * 86_400_000).toISOString();
}

/**
 * Deep-clone a JSON-loaded object and replace every `*_offset_days` field
 * with a corresponding date field (e.g. `created_at_offset_days` → `created_at`).
 */
function resolveDates<T>(obj: T): T {
  const json = JSON.stringify(obj);
  const parsed = JSON.parse(json);
  return walkAndResolve(parsed) as T;
}

function walkAndResolve(node: unknown): unknown {
  if (Array.isArray(node)) {
    return node.map(walkAndResolve);
  }
  if (node !== null && typeof node === "object") {
    const record = node as Record<string, unknown>;
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(record)) {
      if (key.endsWith("_offset_days") && typeof value === "number") {
        // "created_at_offset_days" → "created_at"
        const dateKey = key.replace("_offset_days", "");
        result[dateKey] = resolveOffset(value);
      } else {
        result[key] = walkAndResolve(value);
      }
    }
    return result;
  }
  return node;
}

// ─── CMS ─────────────────────────────────────────────────────────────
const mockHeroSlides = heroSlidesFixture;
const mockBrandShowcases = brandShowcasesFixture;
const mockSectionSettings = cmsFixture.section_settings;
const mockAnnouncements = cmsFixture.announcements;
const mockTrustItems = cmsFixture.trust_items;

// ─── Delivery ────────────────────────────────────────────────────────
const mockDeliveryZones = deliveryFixture.zones;
const mockDeliveryMethods = deliveryFixture.methods;
const mockDeliveryConfig = deliveryFixture.config;

// ─── Products (with date hydration) ─────────────────────────────────
const mockProducts = resolveDates(productsFixture);

// ─── Route → Mock data mapping ───────────────────────────────────────
const mockRoutes: Record<string, unknown> = {
  "/api/cms/hero-slides": mockHeroSlides,
  "/api/cms/brand-showcases": mockBrandShowcases,
  "/api/delivery/zones": mockDeliveryZones,
  "/api/delivery/methods": { methods: mockDeliveryMethods },
  "/api/delivery/config": mockDeliveryConfig,
  "/api/catalog/products": mockProducts,
  "/api/cms/section-settings": mockSectionSettings,
  "/api/cms/announcements": mockAnnouncements,
  "/api/cms/trust-items": mockTrustItems,
  "/api/cms/categories": cmsFixture.categories,
  "/api/cms/flash-deals": cmsFixture.flash_deals,
  "/api/cms/featured": cmsFixture.featured,
  "/api/cms/promo-banners": cmsFixture.promo_banners,
  "/api/cms/social-proof": cmsFixture.social_proof,
  "/api/cms/app-banner": cmsFixture.app_banner,
};

/**
 * Look up mock data for a given storefront API path.
 * Supports query-string filtering for catalog products.
 */
export function getMockData(path: string): unknown | undefined {
  const [clean, qs] = path.split("?");
  const route = clean.replace(/\/+$/, "");

  // Single product lookup: /api/catalog/products/:id
  if (route.startsWith("/api/catalog/products/")) {
    const productId = route.split("/").pop();
    const product = mockProducts.products.find((p) => p.id === productId);
    if (!product) return undefined;
    return product;
  }

  const data = mockRoutes[route];
  if (data === undefined) return undefined;

  // Filter promo banners by position
  if (route === "/api/cms/promo-banners" && qs) {
    const params = new URLSearchParams(qs);
    const position = params.get("position");
    if (position) {
      const banners = (data as { banners: Array<{ position: string }> }).banners;
      return { banners: banners.filter((b) => b.position === position) };
    }
  }

  // Filter featured collections by type when requested
  if (route === "/api/cms/featured" && qs) {
    const params = new URLSearchParams(qs);
    const type = params.get("type");
    if (type) {
      const collections = (data as { collections: Array<{ type: string }> }).collections;
      return { collections: collections.filter((c) => c.type === type) };
    }
  }

  // Apply client-side filtering for catalog products mock
  if (route === "/api/catalog/products" && qs) {
    const params = new URLSearchParams(qs);
    const brand = params.get("brand");
    const category = params.get("category");
    const ids = params.get("ids");
    const allProducts = (data as typeof mockProducts).products;
    let products = allProducts;
    if (ids) {
      const idList = ids.split(",");
      products = products.filter((p) => idList.includes(p.id));
    }
    const filtered = products.filter((p) => {
      if (brand && p.brand !== brand) return false;
      if (category && p.category !== category) return false;
      return true;
    });
    return { products: filtered, pagination: { page: 1, limit: 20, total: filtered.length } };
  }

  return data;
}
